"use client";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLocaleCtx } from "@/contexts/locale";
import { useTranslation } from "@/i18n/client";

import { feApi } from "@/app/api/utils/api";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import { FocusEvent, useState } from "react";
import { useForm } from "react-hook-form";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { toast } from "sonner";
import { z } from "zod";
import { ArrowLeft, ArrowRight } from "../icons/arrow";
import { Image } from "../shared/image";
import { PhoneInput } from "../ui/phone-input";
import { ScrollArea } from "../ui/scroll-area";
import { Textarea } from "../ui/textarea";
import { FormInput } from "./form-input";
import GradientBorder from "../common/gradient-border";

const labelClass =
  "absolute ltr:left-4 rtl:right-4 top-0 flex h-full transform items-center z-[-10] pointer-event-none text-sm transition-all group-focus-within:h-1/2 group-focus-within:-translate-y-[10%] group-focus-within:pl-2 group-focus-within:text-[8px] peer-valid:h-1/2 peer-valid:-translate-y-[10%] peer-valid:pl-2 peer-valid:text-[8px]";

const MAX_WORDS = 250; // NOTE:: updating this value will also require updating the i18n error key in locale files
const MIN_WORDS = 10; // NOTE:: updating this value will also require updating the i18n error key in locale files

export function isValidMessage(value: string) {
  const sanitizedText = value

    .replace(/^\s+/u, " ") // Replace leading spaces with a single space (includes Unicode)
    .replace(/[\s\u200B]+/g, " ") // Replace multiple whitespace characters with a single space (including Unicode and zero-width spaces)
    .trimStart(); // Optionally remove leading space if not needed

  const words = sanitizedText.split(" ").filter((word) => word.length > 0);

  return words.length >= MIN_WORDS && words.length <= MAX_WORDS;
}

const formSchema = z.object({
  firstName: z.string().min(2, {
    message: "Please enter a valid first name",
  }),
  lastName: z.string().min(2, {
    message: "Please enter a valid last name",
  }),
  email: z.string({ message: "Please enter a valid email address" }).email({
    message: "Please enter a valid email address",
  }),
  phone: z.string().min(2, {
    message: "Please enter a valid phone number",
  }),
  reason: z.string().min(2, {
    message: "Please enter a reason",
  }),
  message: z.string().min(1),
});

type Props = {
  show: boolean;
  onClose: () => void;
};
export default function ContactUsForm({ show, onClose }: Props) {
  const { lang } = useLocaleCtx();
  const { t } = useTranslation(lang, "contact-us");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      message: "",
      phone: "",
      reason: "",
    },
    mode: "all",
  });

  const phone = form.watch("phone") || "";

  const loadingMsg = t("Sending your message. Please wait");
  const successMsg = t("Thank you for your message. We will get back to you shortly");
  function onSubmit(values: z.infer<typeof formSchema>) {
    if (!isPossiblePhoneNumber(values.phone)) {
      form.setError("phone", {
        type: "manual",
        message: "Please enter a valid phone number",
      });

      return;
    }

    if (!isValidMessage(values.message)) {
      form.setError("message", {
        type: "manual",
        message: `Please enter a message with at least ${MIN_WORDS} words`,
      });

      return;
    }

    setIsSubmitting(true);
    const errorMsg = t("An error occurred while sending your message. Please try again later");

    setError(null);
    onClose();

    toast.promise(feApi.post(values, `/v1/${lang}/contact-us`), {
      loading: loadingMsg,
      success: () => {
        form.reset();

        if (show) {
          setIsSuccess(true);
        }

        return successMsg;
      },
      error: () => {
        setError(errorMsg);
        return errorMsg;
      },
      finally: () => {
        setIsSubmitting(false);
      },
      className: "text-lg rtl:sm:text-xl",
    });
  }

  return (
    <div className="">
      <AlertDialog
        onOpenChange={() => {
          setIsSuccess(false);
          onClose();
        }}
        open={show}
      >
        <AlertDialogContent
          overlay={
            {
              // className: "sm:z-[9999]",
            }
          }
          className="block w-full max-w-[700px] overflow-hidden !rounded-3xl bg-gradient-to-b from-[#6e4b8e] via-[#8361a5] to-[#6e4b8e] !py-0 px-0"
        >
          <GradientBorder radius="24px" />
          <ScrollArea
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="relative flex h-full max-h-dvh w-full flex-col sm:max-h-[80vh] lg:max-h-[80vh]"
          >
            <div className="absolute top-4 ltr:right-4 rtl:left-4">
              <Button
                className="z-10 rounded-full bg-secondary p-2 transition-all duration-200 ease-in hover:bg-secondary/80"
                onClick={() => {
                  setIsSuccess(false);
                  onClose();
                }}
                variant={"outline"}
                size={"icon"}
              >
                <X className="text-background" />
              </Button>
            </div>
            {isSubmitting ? (
              <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-4 px-4">
                <Image
                  src={"/assets/spin.gif"}
                  alt=""
                  width={200}
                  height={200}
                  className="max-w-[80px]"
                />
                <p className="text-center text-secondary xl:text-lg">{loadingMsg}</p>
              </div>
            ) : (
              <>
                {isSuccess ? (
                  <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-4 px-4">
                    <Image
                      src={"/assets/success.gif"}
                      alt=""
                      width={200}
                      height={200}
                      className="max-w-[80px]"
                    />
                    <p className="text-center text-secondary xl:text-lg">{successMsg}</p>

                    <div className="mt-4">
                      <Button
                        onClick={() => {
                          setIsSuccess(false);
                        }}
                        variant={"outline"}
                        className="h-12 gap-2 !rounded-full border-secondary/30 px-4 font-normal uppercase text-secondary/70 opacity-80 transition-all duration-150 ease-in hover:border-secondary hover:text-secondary/90 ltr:font-sans"
                      >
                        <ArrowLeft />
                        {t("back")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col gap-6 px-4 py-8 pt-10 sm:px-6">
                      <AlertDialogHeader className="flex flex-col gap-4">
                        {error && (
                          <p className="mx-auto flex h-14 w-full items-center justify-center rounded-[30px] bg-red-500/10 px-4 text-center text-xs text-red-500">
                            {error}
                          </p>
                        )}
                        <AlertDialogTitle className="text-start rtl:!text-right sm:rtl:text-right">
                          {t("title")}
                        </AlertDialogTitle>
                      </AlertDialogHeader>
                      <div>
                        <Form {...form}>
                          <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="space-y-4 sm:space-y-8"
                          >
                            <div className="grid gap-4 sm:grid-cols-2">
                              <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormControl>
                                      <div className="group relative">
                                        <FormInput
                                          type="text"
                                          {...field}
                                          strict
                                          ignoreNumbers
                                          required
                                        />
                                        <FormLabel className={labelClass}>
                                          {t("firstname")}
                                          <span className="text-red-500">*</span>
                                        </FormLabel>
                                      </div>
                                    </FormControl>

                                    <FormMessage t={t} />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormControl>
                                      <div className="group relative">
                                        <FormInput
                                          strict
                                          ignoreNumbers
                                          type="text"
                                          {...field}
                                          required
                                        />
                                        <FormLabel className={labelClass}>
                                          {t("lastname")}
                                          <span className="text-red-500">*</span>
                                        </FormLabel>
                                      </div>
                                    </FormControl>

                                    <FormMessage t={t} />
                                  </FormItem>
                                )}
                              />
                            </div>
                            <FormField
                              control={form.control}
                              name="email"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <div className="group relative">
                                      <FormInput emailCharOnly type="text" {...field} required />
                                      <FormLabel className={labelClass}>
                                        {t("email")}
                                        <span className="text-red-500">*</span>
                                      </FormLabel>
                                    </div>
                                  </FormControl>

                                  <FormMessage t={t} />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="phone"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <div className="group">
                                      <PhoneInput
                                        {...field}
                                        className="flex items-center gap-4"
                                        // smartCaret={false}
                                        ref={undefined}
                                        inputComponent={FormInput}
                                        onChange={(value) => {
                                          form.clearErrors("phone");
                                          field.onChange(value);
                                        }}
                                        onBlur={(e: FocusEvent<HTMLInputElement>) => {
                                          if (!isPossiblePhoneNumber(e.target.value)) {
                                            form.setError("phone", {
                                              type: "manual",
                                              message: "Please enter a valid phone number",
                                            });
                                          }
                                        }}
                                      />

                                      {/* <FormLabel className={labelClass}>
                            Phone number <span className="text-red-500">*</span>
                          </FormLabel> */}
                                    </div>
                                  </FormControl>

                                  <FormMessage t={t} />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="reason"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <div className="group relative">
                                      <FormInput
                                        type="text"
                                        {...field}
                                        required
                                        strict
                                        className="peer h-16 w-full border-secondary/20 px-4 text-sm outline-none"
                                      />
                                      <FormLabel className={labelClass}>
                                        {t("reason")}
                                        <span className="text-red-500">*</span>
                                      </FormLabel>
                                    </div>
                                  </FormControl>

                                  <FormMessage t={t} />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="message"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <div className="group relative">
                                      <Textarea
                                        {...field}
                                        required
                                        className="peer h-[200px] w-full resize-none rounded-[40px] border-secondary/20 px-4 pt-10 text-sm outline-none"
                                        cols={30}
                                        rows={40}
                                        onChange={(e) => {
                                          const value = e.target.value;

                                          const sanitizedText = value
                                            .replace(/^\s+/g, " ") // Replace leading spaces with a single space
                                            .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
                                            .trimStart(); // Remove leading space if any

                                          const words = sanitizedText
                                            .split(" ")
                                            .filter((word) => word.length > 0);

                                          if (words.length >= MAX_WORDS) {
                                            return;
                                          }

                                          field.onChange(sanitizedText);
                                        }}
                                        onBlur={(e) => {
                                          const value = e.target.value;

                                          if (!isValidMessage(value)) {
                                            form.setError("message", {
                                              type: "manual",
                                              message: `Please enter a message with at least ${MIN_WORDS} words`,
                                            });

                                            return;
                                          }
                                          form.clearErrors("message");
                                        }}
                                      />
                                      <FormLabel
                                        className={cn(labelClass, "mb-auto items-start pt-6")}
                                      >
                                        {t("message")} <span className="text-red-500">*</span>
                                      </FormLabel>
                                    </div>
                                  </FormControl>

                                  <FormMessage t={t} />
                                </FormItem>
                              )}
                            />
                            <div className="flex items-center justify-end pt-8">
                              <Button
                                type="submit"
                                size={"lg"}
                                variant={"secondary"}
                                disabled={
                                  !form.formState.isValid ||
                                  isSubmitting ||
                                  !isValidMessage(form.watch("message")) ||
                                  !isPossiblePhoneNumber(phone)
                                }
                                className="z-20 h-14 gap-2 rounded-[70px] bg-white uppercase text-black disabled:cursor-not-allowed ltr:font-playfair rtl:font-cairo"
                              >
                                {t("submit")}
                                <ArrowRight fill="currentColor" />
                              </Button>
                            </div>
                          </form>
                        </Form>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </ScrollArea>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
